import { useState, type FormEvent } from "react";
import Reveal from "@/components/common/Reveal";
import RevealStagger from "@/components/common/RevealStagger";
import Button from "@/components/common/Button";
import GridBackdrop from "@/components/visual/GridBackdrop";
import { ArrowRight } from "@/components/common/icons";
import { CHANNELS, SUPPORT_EMAIL } from "@/lib/data";
import { usePageMeta } from "@/lib/usePageMeta";
import { useLocale } from "@/i18n/useLocale";
import { supportFor } from "@/content/support";
import type { SupportContent, SupportTopicKey } from "@/content/types";
import { useBreadcrumbStructuredData } from "@/lib/seo";
import { env } from "@/lib/env";
import { cx } from "@/lib/cx";

function SupportArt(): JSX.Element {
  // Decorative desktop-only support glyph — chat bubble + headphone
  // arc + three dots, on a brand-glow halo.
  return (
    <div
      className="hidden lg:flex lg:items-center lg:justify-center relative mx-auto"
      style={{ width: "clamp(180px, 16vw, 240px)", height: "clamp(180px, 16vw, 240px)" }}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgb(var(--brand) / 0.32) 0%, rgb(var(--brand) / 0.08) 45%, transparent 70%)",
          filter: "blur(8px)",
        }}
      />
      <svg viewBox="0 0 200 200" width="86%" height="86%" fill="none" className="relative">
        <circle cx="100" cy="100" r="86" fill="rgb(var(--card))" stroke="rgb(var(--border-strong))" strokeWidth="1.4" />
        <circle cx="100" cy="100" r="68" fill="none" stroke="rgb(var(--brand) / 0.35)" strokeWidth="1" strokeDasharray="3 5" />
        <path d="M48 100a52 52 0 0 1 104 0" stroke="rgb(var(--brand))" strokeWidth="3.5" strokeLinecap="round" />
        <rect x="40" y="92" width="18" height="34" rx="6" fill="rgb(var(--brand))" />
        <rect x="142" y="92" width="18" height="34" rx="6" fill="rgb(var(--brand))" />
        <path
          d="M70 80h60a14 14 0 0 1 14 14v22a14 14 0 0 1-14 14h-30l-16 14v-14h-14a14 14 0 0 1-14-14V94a14 14 0 0 1 14-14z"
          fill="rgb(var(--bg))"
          stroke="rgb(var(--brand))"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <circle cx="86" cy="105" r="3.5" fill="rgb(var(--brand))" />
        <circle cx="100" cy="105" r="3.5" fill="rgb(var(--brand))" />
        <circle cx="114" cy="105" r="3.5" fill="rgb(var(--brand))" />
      </svg>
    </div>
  );
}

function ChannelCards({ t }: { t: SupportContent }): JSX.Element {
  return (
    <RevealStagger className="grid gap-3 grid-cols-1 sm:grid-cols-2 w-full" step={80}>
      {CHANNELS.map((c) => (
        <div
          key={c.key}
          className="glass-card glass-card-hover h-full flex flex-col p-5 md:p-6"
        >
          <div className="font-mono text-[10.5px] text-fg-dim tracking-[0.14em]">CHANNEL</div>
          <h3 className="text-h3 font-display font-semibold mt-2">{t.channels[c.key].name}</h3>
          <div className="font-mono text-[11px] text-success mt-1.5 tracking-[0.06em]">
            → {t.channels[c.key].meta}
          </div>
          <p className="text-fg-muted mt-3.5 text-[13.5px] flex-1">
            {c.key === "email" ? SUPPORT_EMAIL : t.channels[c.key].desc}
          </p>
          <Button
            variant="primary"
            href={c.ctaHref}
            className="mt-3.5 w-full justify-center"
          >
            {t.channels[c.key].cta}
          </Button>
        </div>
      ))}
    </RevealStagger>
  );
}

function Hero({ t }: { t: SupportContent }): JSX.Element {
  return (
    <section className="relative pt-[calc(var(--header-h,72px)+72px)] pb-12 overflow-hidden lg:min-h-screen lg:flex lg:items-center">
      <div className="brand-glow" style={{ height: 360 }} aria-hidden="true" />
      <div className="container-page relative z-[2] lg:w-full">
        <div className="flex flex-col gap-10 lg:gap-14">
          {/* Row 1: title (left) + support icon (right) */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16">
            <div className="flex-1 max-w-[780px]">
              <h1
                className="font-display font-bold"
                style={{ fontSize: "clamp(40px, 4.5vw, 72px)", lineHeight: 0.96, letterSpacing: "-0.04em" }}
              >
                {t.hero.line1}
                <br />
                <span className="text-brand-gradient">{t.hero.line2Highlight}</span>
              </h1>
              <p className="text-fg-muted text-base md:text-[18px] max-w-[560px] mt-5">
                {t.hero.lede}
              </p>
            </div>
            <SupportArt />
          </div>

          {/* Row 2: 2 channel boxes centered under both components */}
          <div className="max-w-[820px] w-full mx-auto">
            <ChannelCards t={t} />
          </div>
        </div>
      </div>
    </section>
  );
}

function buildSupportMailto(name: string, fromEmail: string, topic: string, message: string): string {
  const subject = `[Support] ${topic} — ${name || "trader enquiry"}`;
  const bodyLines = [
    `Name: ${name}`,
    `Email: ${fromEmail}`,
    `Topic: ${topic}`,
    "",
    message,
  ];
  const params = new URLSearchParams({ subject, body: bodyLines.join("\n") });
  return `mailto:${SUPPORT_EMAIL}?${params.toString().replace(/\+/g, "%20")}`;
}

function FaqAndForm({ t }: { t: SupportContent }): JSX.Element {
  const [name, setName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [topic, setTopic] = useState("Account");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    window.location.href = buildSupportMailto(name, fromEmail, topic, message);
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  }

  return (
    <section className="section-y-lg bg-bg-2 border-y border-border">
      <div className="container-page">
        <div className="grid gap-4 support-grid">
          {/* FAQ redirect tile */}
          <Reveal>
            <div className="glass-card h-full flex flex-col p-5 md:p-6">
              <h3 className="text-h3 font-display font-semibold">
                {t.faqPanel.titleLead}
                <span className="text-brand-gradient">{t.faqPanel.titleHighlight}</span>
              </h3>
              <p className="text-fg-muted mt-3.5 text-[13.5px] flex-1">
                {t.faqPanel.body}
              </p>
              <Button
                variant="primary"
                href={env.traderFaqUrl}
                className="mt-5 w-full justify-center"
              >
                Open FAQs <ArrowRight />
              </Button>
            </div>
          </Reveal>

          {/* Contact form (mailto) */}
          <Reveal delay={80}>
            <form onSubmit={onSubmit} className="glass-card h-full flex flex-col p-5 md:p-6">
              <h3 className="text-h3 font-display font-semibold">{t.form.heading}</h3>
              <div className="mt-3.5 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    placeholder={t.form.namePlaceholder}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-bg-2 border border-border rounded-md px-3.5 py-3 text-fg font-sans text-sm outline-none focus:border-brand-emphasis"
                  />
                  <input
                    placeholder={t.form.emailPlaceholder}
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className="bg-bg-2 border border-border rounded-md px-3.5 py-3 text-fg font-sans text-sm outline-none focus:border-brand-emphasis"
                  />
                </div>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="bg-bg-2 border border-border rounded-md px-3.5 py-3 text-fg font-sans text-sm outline-none focus:border-brand-emphasis"
                >
                  {(["account", "payouts", "rules", "platform", "billing"] as const).map(
                    (k: SupportTopicKey) => (
                      // The VALUE is the stable key and is what reaches the
                      // mailto subject; only the visible label translates, so a
                      // German submission is not an untriageable subject line.
                      <option key={k} value={k}>
                        {t.form.topics[k]}
                      </option>
                    ),
                  )}
                </select>
                <textarea
                  rows={4}
                  required
                  placeholder={t.form.messagePlaceholder}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-bg-2 border border-border rounded-md px-3.5 py-3 text-fg font-sans text-sm outline-none focus:border-brand-emphasis resize-y"
                />
                <Button variant="primary" type="submit" className={cx("w-full justify-center")}>
                  {sent ? t.form.sent : t.form.submit}
                </Button>
                <div className="font-mono text-fg-muted text-[10.5px] text-center tracking-[0.06em]">
                  {t.form.footnote}
                </div>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function SupportPage(): JSX.Element {
  const t = supportFor(useLocale());
  usePageMeta({
    title: t.meta.title,
    description: t.meta.description,
  });
  useBreadcrumbStructuredData([
    { name: "Home", url: "/" },
    { name: "Support", url: "/support" },
  ]);
  return (
    <>
      <GridBackdrop />
      <div className="animate-page-in relative z-[1]">
        <Hero t={t} />
        <FaqAndForm t={t} />
      </div>
    </>
  );
}
